/**
 * Map discount % → tailwind class pair (background + foreground for the
 * "-NN%" badge, plus a foreground for the final price). Steam itself uses
 * a single green; we lean into a small gradient so 75%-off Black Friday
 * deals stand out from a routine 5% nudge.
 *
 * Buckets:
 *   1–9%  : muted teal — barely a sale.
 *   10–24%: Steam green — the standard "on sale" look.
 *   25–49%: lime/yellow — actually meaningful.
 *   50–74%: orange — substantial.
 *   75%+  : red — eye-catcher.
 */
function discountTone(discount: number): {
  badgeBg: string;
  badgeFg: string;
  finalFg: string;
} {
  if (discount >= 75) {
    return {
      badgeBg: "bg-[#5c1a1a]",
      badgeFg: "text-[#ff7575]",
      finalFg: "text-[#ff7575]",
    };
  }
  if (discount >= 50) {
    return {
      badgeBg: "bg-[#5c3a14]",
      badgeFg: "text-[#ffa869]",
      finalFg: "text-[#ffa869]",
    };
  }
  if (discount >= 25) {
    // Earlier this used bg-[#4c6b22] but the lighthouse contrast check
    // pegged the pair at 4.49 vs 4.5 required. Darker bg pushes it
    // comfortably over.
    return {
      badgeBg: "bg-[#2d4513]",
      badgeFg: "text-[#beee11]",
      finalFg: "text-[#beee11]",
    };
  }
  if (discount >= 10) {
    // bg-[#3a5a1c] + text-[#a4d007] was 4.36 (under WCAG AA 4.5). Darker
    // bg pushes contrast comfortably over.
    return {
      badgeBg: "bg-[#243a10]",
      badgeFg: "text-[#a4d007]",
      finalFg: "text-[#a4d007]",
    };
  }
  return {
    badgeBg: "bg-[#1f3a4a]",
    badgeFg: "text-[#79c4d8]",
    finalFg: "text-[#79c4d8]",
  };
}

interface GamePriceProps {
  isFree: boolean | null;
  priceFinal: number | null | undefined;
  priceInitial?: number | null;
  /** Discount %, 0..100. */
  priceDiscount?: number | null;
  className?: string;
}

/**
 * Single-cell price renderer used across all game tables.
 *
 * Three states:
 *   - Free → "Free" badge (Steam green).
 *   - On sale (`priceDiscount > 0` and `priceInitial > priceFinal`) →
 *     `-NN%` badge + strikethrough initial + final price.
 *   - Plain → final price only.
 */
export function GamePrice({
  isFree,
  priceFinal,
  priceInitial,
  priceDiscount,
  className = "",
}: GamePriceProps) {
  if (isFree) {
    return (
      <span className={`text-[#beee11] font-semibold text-xs ${className}`}>
        Free
      </span>
    );
  }

  if (priceFinal === null || priceFinal === undefined) {
    return <span className={`text-[#8f98a0] text-xs ${className}`}>—</span>;
  }

  const discount =
    priceDiscount && priceDiscount > 0 && priceInitial && priceInitial > priceFinal
      ? priceDiscount
      : null;

  if (discount !== null && priceInitial) {
    const tone = discountTone(discount);
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-xs whitespace-nowrap ${className}`}
      >
        <span
          className={`rounded px-1 py-0.5 font-semibold ${tone.badgeBg} ${tone.badgeFg}`}
        >
          -{discount}%
        </span>
        <span className="text-[#8f98a0] line-through">
          ${(priceInitial / 100).toFixed(2)}
        </span>
        <span className={`font-semibold ${tone.finalFg}`}>
          ${(priceFinal / 100).toFixed(2)}
        </span>
      </span>
    );
  }

  return (
    <span className={`text-white text-xs ${className}`}>
      ${(priceFinal / 100).toFixed(2)}
    </span>
  );
}
